/**
 * /customer/home — greeting, featured brands, promotions, plus a
 * minimal "View All Activity" link routing into /customer/history.
 *
 * Path D2 cleanup (2026-05-31): removed the redundant "Your cards"
 * carousel (My Cards tab owns that surface) and replaced the inline
 * Recent Activity feed with a single link card so customers still
 * have a one-tap path to their full history without duplicating
 * surface area against the My Cards / Profile flows.
 *
 * All data flows through TanStack Query against existing customer
 * endpoints (no new backend).
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Clock, Megaphone, Crown } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { resolveImageUrl } from '@/lib/api';
import TierBadge from '@/components/membership/TierBadge';
import { expiryLabel } from '@/lib/membershipFmt';

export default function CustomerHomePage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);

  const featuredQ = useQuery({
    queryKey: qk.customerBrandsFeatured(),
    queryFn: () => api.customerBrandsFeatured(token),
    enabled: !!token,
  });

  const membershipsQ = useQuery({
    queryKey: qk.customerMyMemberships(),
    queryFn: () => api.customerMyMemberships(token),
    enabled: !!token,
  });

  const activeMemberships = (membershipsQ.data ?? []).filter(
    (m) => m.effective_status === 'active',
  );

  const greetingName = (user?.name ?? '').split(' ')[0] || 'there';

  return (
    <div className="space-y-10" data-testid="customer-home-page">
      {/* Greeting */}
      <div>
        <div
          className="text-xs uppercase tracking-widest text-gold font-mono mb-2"
          data-testid="customer-home-membership"
        >
          Membership · {user?.membership_id ?? '—'}
        </div>
        <h1
          className="font-display text-3xl md:text-4xl font-bold"
          data-testid="customer-home-greeting"
        >
          Hi {greetingName} 👋
        </h1>
        <p className="text-sm text-white/55 mt-1.5">
          Welcome back. Here is what is happening with your loyalty journey.
        </p>
      </div>

      {/* OneStamp Pro Phase 1 — Membership chip strip.
          Positioned ABOVE Featured Brands (rationale: personal > discovery).
          Hidden entirely when the customer has zero active memberships. */}
      {activeMemberships.length > 0 ? (
        <section data-testid="customer-home-memberships-section">
          <div className="flex items-end justify-between mb-3">
            <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
              <Crown size={16} className="text-gold" />
              Your Memberships
            </h2>
            <button
              type="button"
              onClick={() => navigate('/customer/profile')}
              data-testid="customer-home-memberships-see-all"
              className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-gold hover:text-gold/80 transition-colors"
            >
              See all <ChevronRight size={12} />
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {activeMemberships.map((m) => (
              <div
                key={m.id}
                data-testid={`customer-home-membership-chip-${m.id}`}
                className="snap-start shrink-0 w-72 rounded-2xl border bg-[#15151B] p-4 hover:border-gold/35 transition-colors"
                style={{
                  borderColor: `${(m.tier?.display_color || '#C9A84C')}55`,
                  background: `linear-gradient(135deg, ${(m.tier?.display_color || '#C9A84C')}11, #15151B 60%)`,
                }}
              >
                <div className="flex items-center gap-3 mb-2.5">
                  {m.brand_logo_url ? (
                    <img
                      src={resolveImageUrl(m.brand_logo_url)}
                      alt=""
                      className="w-10 h-10 rounded-xl border border-white/15 shadow"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-gold/15 border border-gold/30 grid place-items-center text-gold font-bold">
                      {(m.brand_name ?? '?').slice(0, 1)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{m.brand_name}</div>
                    <div className="text-[11px] text-white/55 truncate">{m.plan?.name ?? '—'}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  {m.tier ? <TierBadge tier={m.tier} /> : <span />}
                  <span className="text-[11px] text-white/65 font-mono">
                    {expiryLabel(m)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Featured brands */}
      <section data-testid="customer-featured-section">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-lg font-semibold">Featured brands</h2>
          <button
            type="button"
            onClick={() => navigate('/customer/brands')}
            data-testid="customer-home-see-all-brands"
            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-gold hover:text-gold/80 transition-colors"
          >
            See all <ChevronRight size={12} />
          </button>
        </div>
        {featuredQ.isLoading ? (
          <div className="h-40 rounded-3xl bg-white/[0.03] border border-white/5 animate-pulse" />
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
            {(featuredQ.data ?? []).slice(0, 8).map((b) => (
              <button
                key={b.id}
                onClick={() => navigate(`/customer/brand/${b.id}`)}
                data-testid={`customer-brand-card-${b.id}`}
                className="w-64 shrink-0 rounded-3xl bg-[#15151B] border border-white/8 hover:border-gold/30 transition-all hover:shadow-glow-gold-sm overflow-hidden text-left"
              >
                <div
                  className="h-28 bg-gradient-to-br from-gold/15 to-black/40 relative"
                  style={b.banner_url ? { backgroundImage: `url(${resolveImageUrl(b.banner_url)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : null}
                >
                  {b.logo_url ? (
                    <img
                      src={resolveImageUrl(b.logo_url)}
                      alt=""
                      className="absolute bottom-3 left-3 w-12 h-12 rounded-2xl border border-white/15 shadow-xl bg-black/40"
                    />
                  ) : null}
                </div>
                <div className="px-4 py-3">
                  <div className="font-semibold text-sm truncate">{b.name}</div>
                  <div className="text-xs text-white/45 truncate mt-0.5">
                    {b.description || `${b.branch_count ?? 0} location${(b.branch_count ?? 0) === 1 ? '' : 's'}`}
                  </div>
                </div>
              </button>
            ))}
            {(featuredQ.data ?? []).length === 0 ? (
              <div className="text-sm text-white/40 italic">No featured brands available right now.</div>
            ) : null}
          </div>
        )}
      </section>

      {/* Path D2: "Your cards" carousel removed — My Cards tab owns
          that surface. Recent Activity feed replaced with a single
          minimal link card routing to the full history page. */}
      <section data-testid="customer-activity-link-section">
        <button
          type="button"
          onClick={() => navigate('/customer/history')}
          data-testid="customer-home-view-all-activity"
          className="group w-full flex items-center gap-4 px-5 py-4 rounded-2xl border border-white/8 bg-[#15151B] hover:border-gold/35 hover:bg-gold/[0.04] transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-gold/15 border border-gold/30 grid place-items-center text-gold shrink-0">
            <Clock size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white/90 group-hover:text-white">
              View all activity
            </div>
            <div className="text-xs text-white/50 mt-0.5">
              Every stamp and redemption across your brands
            </div>
          </div>
          <ChevronRight
            size={16}
            className="text-white/45 group-hover:text-gold transition-colors shrink-0"
          />
        </button>
      </section>

      {/* Promotions — Phase W-2 wired */}
      <PromotionsStrip />
    </div>
  );
}

function PromotionsStrip() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const promosQ = useQuery({
    queryKey: qk.customerPromotions(),
    queryFn: () => api.customerPromotions(token),
    enabled: !!token,
  });
  const promos = (promosQ.data ?? []).slice(0, 5);

  return (
    <section data-testid="customer-promotions-section">
      <div className="flex items-end justify-between mb-3">
        <h2 className="font-display text-lg font-semibold">Promotions</h2>
        <button
          type="button"
          onClick={() => navigate('/customer/promotions')}
          className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-gold hover:text-gold/80 transition-colors"
          data-testid="customer-promotions-see-all"
        >
          See all <ChevronRight size={12} />
        </button>
      </div>
      {promosQ.isLoading ? (
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="shrink-0 w-72 h-40 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : promos.length === 0 ? (
        <div
          className="rounded-2xl border border-white/10 bg-[#15151B] px-5 py-7 text-center"
          data-testid="customer-promotions-empty"
        >
          <Megaphone size={20} className="mx-auto text-gold/55 mb-2" />
          <div className="text-sm font-medium">No active promotions</div>
          <div className="text-xs text-white/45 mt-1">
            New campaigns drop every few weeks. Check back soon.
          </div>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
          {promos.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => navigate(`/customer/promotions/${p.id}`)}
              data-testid={`customer-promo-card-${p.id}`}
              className="relative shrink-0 w-72 h-40 rounded-2xl overflow-hidden border border-white/10 bg-[#15151B] hover:border-gold/35 transition-colors text-left snap-start"
            >
              {p.image_url ? (
                <img
                  src={resolveImageUrl(p.image_url)}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : null}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-gold mb-1">
                  {p.brand_name}
                </div>
                <div className="font-display text-base font-semibold leading-tight line-clamp-2">
                  {p.title}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
